<template>
  <div class="h5-user-detail">
    <!-- 顶部导航栏 -->
    <div class="header">
      <h1>设备查询</h1>
      <button class="back-btn" @click="handleBack">
        <el-icon><ArrowLeft /></el-icon>
      </button>
    </div>
    
    <!-- 内容区域 -->
    <div class="content">
      <!-- 查询表单 -->
      <div class="query-form">
        <!-- 用户查询 -->
        <div class="form-group">
          <label>用户查询</label>
          <div class="input-group">
            <input 
              v-model="userQuery" 
              placeholder="输入手机号或用户ID" 
              class="form-input"
            />
            <button @click="queryByUser" class="query-btn">查询</button>
          </div>
        </div>
        
        <!-- 设备查询 -->
        <div class="form-group">
          <label>设备查询</label>
          <div class="input-group">
            <input 
              v-model="deviceQuery" 
              placeholder="输入设备ID或物理编码，多个用逗号分隔" 
              class="form-input"
            />
            <button @click="queryByDevice" class="query-btn">查询</button>
          </div>
        </div>
      </div>
      
      <!-- 总数和复制按钮 -->
      <div class="total-section">
        <div class="total-info">
          共 <span class="total-count">{{ total }}</span> 台设备
        </div>
        <button 
          v-if="total > 0" 
          @click="copyAllPhysicalCodes"
          class="copy-all-btn"
        >
          一键复制编号
        </button>
      </div>
      
      <!-- 设备列表 -->
      <div class="device-list">
        <div v-if="loading" class="loading">加载中...</div>
        <div v-else-if="devices.length === 0" class="empty">暂无设备数据</div>
        <div 
          v-else 
          class="device-item" 
          v-for="device in devices" 
          :key="device.id"
        >
          <div class="device-info">
            <div class="info-item">
              <span class="label">设备ID:</span>
              <span class="value">{{ device.id }}</span>
            </div>
            <div class="info-item">
              <span class="label">物理编码:</span>
              <span class="value">{{ device.code }}</span>
              <button 
                @click="copyPhysicalCode(device.code)"
                class="copy-btn"
                title="复制物理编码"
              >
                复制
              </button>
            </div>
            <div class="info-item">
              <span class="label">剩余时长:</span>
              <span class="value">{{ getRemainingDuration(device.remainingDuration) }} 小时</span>
            </div>
              <div class="info-item">
              <span class="label">创建时间:</span>
              <span class="value">{{ device.sysCreateTime }}</span>
            </div>
          </div>
        </div>
        
        <!-- 加载更多 -->
        <div 
          v-if="hasMore && !loading" 
          class="load-more" 
          @click="loadMore"
        >
          加载更多
        </div>
        <div v-else-if="!hasMore && devices.length > 0" class="no-more">
          已加载全部数据
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { ElMessage, ElIcon } from 'element-plus';
import { ArrowLeft } from '@element-plus/icons-vue';
import CONFIG_API from './api.json';
import request from '@/utils/request';
import router from '@/router';

// 响应式数据
const userQuery = ref('');
const deviceQuery = ref('');
const devices = ref([]);
const loading = ref(false);
const hasMore = ref(true);
const page = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 计算剩余时长
const getRemainingDuration = (seconds) => {
  if (Number.isNaN(seconds) || seconds < 0) return '0天0时0分';

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days) parts.push(`${days}天`);
  if (hours) parts.push(`${hours}时`);
  if (minutes) parts.push(`${minutes}分`);

  return parts.join(" ") || ('0天0时0分');
};


// 防抖函数
const debounce = (func, delay = 200) => {
  let timeoutId = null;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

// 检测加载更多元素是否进入视口
const checkLoadMoreInView = () => {
  const loadMoreEl = document.querySelector('.load-more');
  if (!loadMoreEl) return false;

  const rect = loadMoreEl.getBoundingClientRect();
  const viewHeight = window.innerHeight || document.documentElement.clientHeight;
  return rect.top <= viewHeight + 50;
};
// 判断输入的手机号是否是手机号格式
const isMobile = (str) => {
  return /^1[3456789]\d{9}$/.test(str);
};

// 优化滚动监听
const handleScroll = debounce(() => {
  if (loading.value || !hasMore.value) return;
  if (checkLoadMoreInView()) {
    loadMore();
  }
});

// 加载更多数据
const loadMore = () => {
  // 核心防重：正在加载 或 没有更多 → 直接return
  if (loading.value || !hasMore.value) return;

  requestFn({
    pageDomain: {
      pageNum: page.value,
      pageSize: pageSize.value,
    },
    userMobile: userQuery.value,
    id: deviceQuery.value,
    isLoadMore: true
  });
};

// 根据用户查询
const queryByUser = () => {
  if (!userQuery.value) return;

  // 重置所有状态
  devices.value = [];
  page.value = 1;
  hasMore.value = true;

  requestFn({   
    pageDomain: {
      pageNum: page.value,
      pageSize: pageSize.value,
    },  
    userMobile: isMobile(userQuery.value) ? userQuery.value : '',
    userNo: isMobile(userQuery.value) ? '' : userQuery.value,
    id: deviceQuery.value,
    isLoadMore: false
  });
};

// 根据设备查询
const queryByDevice = () => {
  if (!deviceQuery.value) return;

  // 重置所有状态
  devices.value = [];
  page.value = 1;
  hasMore.value = true;

  requestFn({
    pageDomain: {
      pageNum: page.value,
      pageSize: pageSize.value,
    },  
    userMobile: isMobile(userQuery.value) ? userQuery.value : '',
    userNo: isMobile(userQuery.value) ? '' : userQuery.value,
    id: deviceQuery.value,
    isLoadMore: false
  });
};

// 查询请求（统一入口，loading 只在这里控制）
const requestFn = (params) => {
  // 统一开启 loading
  loading.value = true;

  request.post(CONFIG_API.queryUrl.url, {...params,status:'0'}).then(res => {
    if (res.code === 200) {
      const list = res.data?.data || [];
      if (params.isLoadMore) {
        // 滚动加载：追加
        devices.value = [...devices.value, ...list];
      } else {
        // 全新查询：覆盖
        devices.value = list;
      }

      // 正确判断是否还有下一页
      total.value = res.data.total || 0;
      const currentPage = params.pageDomain?.pageNum || 1;
      const size = params.pageDomain?.pageSize || pageSize.value;
      hasMore.value = total.value > currentPage * size;

      // 页码自增
      page.value = currentPage + 1;
    } else {
      ElMessage.error(res.msg || '查询失败');
    }
  }).catch(err => {
    console.error('查询失败:', err);
    ElMessage.error('查询失败');
  }).finally(() => {
    // 统一关闭 loading
    loading.value = false;
  });
};

// 复制单个物理编码
const copyPhysicalCode = (code) => {
  navigator.clipboard.writeText(code).then(() => {
    ElMessage.success('物理编码已复制到剪贴板');
  }).catch(err => {
    console.error('复制失败:', err);
    ElMessage.error('复制失败，请手动复制');
  });
};

// 复制所有物理编码
const copyAllPhysicalCodes = () => {
  const codes = devices.value.map(device => device.physicalCode).join('\n');
  navigator.clipboard.writeText(codes).then(() => {
    ElMessage.success('所有物理编码已复制到剪贴板');
  }).catch(err => {
    console.error('复制失败:', err);
    ElMessage.error('复制失败，请手动复制');
  });
};

// 返回上一页
const handleBack = () => {
  router.push('/h5/workPlace');
};

// 生命周期
onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
//   loadMore();
// 查看路由参数，有则根据参数查询设备列表
const userNo = new URLSearchParams(window.location.search).get('userNo');
if (userNo) {
  userQuery.value = userNo;
  queryByUser();
}
});

onUnmounted(() => {
  
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
.h5-user-detail {
  min-height: 100vh;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  font-family: Arial, sans-serif;
  overflow-x: hidden;
}

.header {
  position: relative;
  background-color: #409eff;
  color: white;
  padding: 16px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.back-btn {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

.content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.query-form {
  background-color: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #303133;
  font-size: 14px;
}

.input-group {
  display: flex;
  gap: 12px;
}

.form-input {
  flex: 1;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.query-btn {
  padding: 0 20px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s;
}

.query-btn:hover {
  background-color: #66b1ff;
}

.total-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0;
  padding: 16px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.total-info {
  font-size: 14px;
  color: #606266;
}

.total-count {
  font-weight: 600;
  color: #303133;
  margin: 0 4px;
}

.copy-all-btn {
  padding: 8px 16px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.copy-all-btn:hover {
  background-color: #66b1ff;
}

.device-list {
  margin-top: 20px;
}

.loading, .empty, .load-more, .no-more {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}

.load-more {
  color: #409eff;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.3s;
}

.load-more:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.device-item {
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
}

.device-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.device-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label {
  color: #606266;
  font-size: 14px;
}

.value {
  color: #303133;
  font-weight: 500;
  font-size: 14px;
  flex: 1;
  text-align: right;
  margin-right: 12px;
}

.copy-btn {
  padding: 4px 12px;
  background-color: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.copy-btn:hover {
  background-color: #e6f7ff;
  border-color: #91d5ff;
}

/* 适配移动端样式 */
@media (max-width: 768px) {
  .content {
    padding: 12px;
  }
  
  .query-form {
    padding: 16px;
  }
  
  .input-group {
    flex-direction: column;
  }
  
  .query-btn {
    padding: 12px;
  }
  
  .total-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .copy-all-btn {
    align-self: flex-end;
  }
  
  .device-item {
    padding: 16px;
  }
}
</style>